/**
 * **UI 的过渡动画。**
 *
 * ## 规矩不是这里定的，是 `visual-design.md` §6 早就写好的
 *
 * | 项 | 值 |
 * | --- | --- |
 * | 时长 | **90–220 ms**。「不要有 400ms 的柔和过渡」 |
 * | 曲线 | **急停**（`easeOutExpo` 的变体）/ 阶跃 |
 * | 过冲（回弹） | **试过了，被否**。手牌那版欠阻尼弹簧评审的原话是「抖动也删掉吧，不好看」 |
 * | 位移 | **小**。印刷品的运动是**压**，不是**飞** |
 *
 * 也就是说：状态切换**必须**有过渡（直接跳变是"网页控件"的观感），
 * 但过渡得**短、急停、不过冲、位移极小**。这三条一起才是"印刷机"，
 * 少任何一条都会滑向"平滑动效的 App"。
 *
 * ## 为什么是一个模块级的步进器
 *
 * 动画要有人每帧推。把 `update(dt)` 一路穿到"屏幕 → 控件 → 控件内部的值"
 * 会让每个组件、每个屏幕都多一个只为了转发的方法，而**漏掉一个**
 * 的表现是"这个控件的动画卡住"——那种 bug 很难看出是谁漏了。
 *
 * 所以这里反过来：控件自己登记进来，宿主每帧调一次 `stepMotion(dt)`。
 * 项目里只有一个 ticker（`main.ts`），所以这个"每帧"没有第二个来源。
 */

import { mixHex } from '../palette';
import type { StampInk } from './ink';
import { t } from '../../i18n';

/**
 * 时长（毫秒）。**只有这三档**，调用点不许写数字。
 *
 * 区间来自 §6 的 90–220：`fast` 是鼠标掠过控件的量级（再慢会"粘手"），
 * `slow` 是整屏级的位移（再快会显得是跳变）。
 */
export const DURATION = {
  /** 控件本体的状态过渡。鼠标掠过就走完 */
  fast: 110,
  /** 停下来之后才看得清的东西（提示条、徽章） */
  normal: 160,
  /** 整屏级的位移 */
  slow: 220,
} as const;

/**
 * **急停**：`easeOutCubic`。
 *
 * ⚠️ 不用 `easeInOut`。那是最典型的"现代 App 缓动"，它传达的是
 * "流畅、友好、无摩擦"——和这套视觉正好相反。印刷机的运动是**撞停**的。
 *
 * ⚠️ 不过冲（overshoot）。§6 明确记着弹簧那版被否了。
 */
export function easeOut(t: number): number {
  const k = t < 0 ? 0 : t > 1 ? 1 : t;
  return 1 - (1 - k) ** 3;
}

/** 一个朝目标滑过去的标量。**非线性**：走的是 `easeOut` */
export interface Animated {
  readonly value: number;
  /** 当前是不是还在动 */
  readonly moving: boolean;
  /** 设一个新目标。已经在同一目标上就什么都不做（免得每帧重启一次过渡） */
  to(target: number, durationMs: number): void;
  /** 立刻落到目标上（禁用/首帧用，避免"一进场所有控件一起滑"） */
  snap(target: number): void;
  /** 返回 `true` 表示还没停 */
  advance(deltaMS: number): boolean;
}

export function createAnimated(initial: number): Animated {
  let from = initial;
  let target = initial;
  let value = initial;
  let elapsed = 0;
  let duration = 0;

  return {
    get value() {
      return value;
    },
    get moving() {
      return value !== target;
    },
    to(next, durationMs) {
      if (next === target) return;
      // 从**当前值**起步，而不是从上一个起点 —— 否则连续两次状态变化
      // 会让值先跳回去再走，看起来是一次闪烁
      from = value;
      target = next;
      elapsed = 0;
      duration = Math.max(1, durationMs);
    },
    snap(next) {
      from = next;
      target = next;
      value = next;
      elapsed = 0;
      duration = 0;
    },
    advance(deltaMS) {
      if (value === target) return false;
      elapsed += deltaMS;
      const t = elapsed >= duration ? 1 : elapsed / duration;
      value = from + (target - from) * easeOut(t);
      if (t >= 1) {
        value = target;
        return false;
      }
      return true;
    },
  };
}

/**
 * 两块墨之间插值。
 *
 * 颜色过渡走 `mixHex`（线性 RGB）而不是"跳到新颜色"：直接从
 * `LIGHT.dim` 跳到 `LIGHT.base` 是一次闪，而 110ms 的插值读起来是
 * "这块墨被多印了一遍"。
 */
export function mixInk(a: StampInk, b: StampInk, t: number): StampInk {
  if (t <= 0) return a;
  if (t >= 1) return b;
  return {
    fill: mixHex(a.fill, b.fill, t),
    edge: mixHex(a.edge, b.edge, t),
    label: mixHex(a.label, b.label, t),
  };
}

// ============================================================
// 步进器
// ============================================================

/** 还在动的东西。控件在开始过渡时登记自己，停下来就自动移除 */
const running = new Set<{ advance(deltaMS: number): boolean }>();

/**
 * 把某个动画登记进每帧队列。
 *
 * ⚠️ **销毁之前必须 `undrive()`。** 队列只认 `advance()`，看不见那个动画背后
 * 的东西是不是还活着；而"动画还在走、对象已经销毁"的下场是**每一帧都往已销毁
 * 的容器上写位置**（Pixi 销毁时会把 `_position` / `_scale` 置空），
 * 于是抛 `Cannot set properties of null (setting 'x')`。
 * 那个异常是从 `Ticker` 的回调里抛出去的 —— 它**不会再排下一帧**，
 * 整个应用就此冻住（评审报过一次"画面卡死"就是这么来的）。
 */
export function drive(node: { advance(deltaMS: number): boolean }): void {
  running.add(node);
}

/** 把一个动画从每帧队列里摘掉。见 `drive()` 的说明 */
export function undrive(node: { advance(deltaMS: number): boolean }): void {
  running.delete(node);
}

/** 还在动的数量。诊断与测试用 */
export function activeMotion(): number {
  return running.size;
}

/**
 * 每帧推一次。**由 `screenHost` 调用**，别在别处再调一次 ——
 * 推两次等于动画速度翻倍，而那种 bug 只在"改完 UI 倍率重建之后"才现形。
 */
export function stepMotion(deltaMS: number): void {
  if (running.size === 0) return;
  for (const node of [...running]) {
    try {
      if (!node.advance(deltaMS)) running.delete(node);
    } catch (error) {
      /**
       * **最后一个动画抛了异常，其他动画不该跟着死，更不该让整个应用冻住。**
       *
       * 正常路径上 `undrive()` 已经挡住了"往已销毁的对象上写位置"，这一层是
       * 兜底：这一只已经坏了（再走下一帧只会继续抛），摘掉它，别人接着走。
       * 不这么做的后果不是"少一个动画"，而是 `Ticker` 抛出之后不再排下一帧 ——
       * 整个游戏画面停在那里，而控制台只有一条看不出所以然的 TypeError。
       */
      running.delete(node);
      console.error(t('render.motion.error'), error);
    }
  }
}

/** 测试用：把队列清空，免得不同用例之间互相影响 */
export function resetMotion(): void {
  running.clear();
}
